import { useI18n } from "../../../i18n";
import type { MbFrame } from "../lib/live";
import type { RadioStatus } from "../hooks/useMbRadio";
import { toRemote } from "../lib/live";

/**
 * 진짜 보드 거울 — 보드가 연결된 동안 왼쪽 칸에 시뮬레이터 대신 그린다. 보드가 보낸 PF1 줄 그대로(LED 25칸 밝기 · A · B · 로고 · 흔들기 · 기울기 · 센서).
 * MakeCode 시뮬레이터에는 버튼 · 센서 · 기울기 값을 바깥에서 넣는 길이 없어 진짜 보드와 따로 돈다 — 그래서 보이는 보드와 판정하는 보드를 하나로 맞춘다.
 * 모습은 시뮬레이터 보드를 닮게(검은 판 · A/B 단추 · 터치 로고 · 빨간 LED · 금색 핀) 우리가 그린 SVG 다(상류 그림을 가져오지 않는다 · 2026-10-05 사용자 요청).
 */
const ARROW: Record<string, string> = { n: "↑", ne: "↗", e: "→", se: "↘", s: "↓", sw: "↙", w: "←", nw: "↖" };
/** 기울기 → 판이 기우는 모습(위에서 본 보드 — 북쪽으로 기울면 위가 멀어진다) */
const TILT: Record<string, [number, number]> = { n: [14, 0], s: [-14, 0], e: [0, -14], w: [0, 14], ne: [10, -10], nw: [10, 10], se: [-10, -10], sw: [-10, 10] };

const W = 500;
const H = 420;
const LED0X = 170;
const LED0Y = 92;
const LED_STEP = 40;
/** 아래 금색 핀 — 큰 고리 다섯(0 · 1 · 2 · 3V · GND) */
const PINS = [["0", 62], ["1", 156], ["2", 250], ["3V", 344], ["GND", 438]] as const;

export function BoardMirror({ frame }: { frame: MbFrame | null }) {
  const { t } = useI18n();
  const leds = frame?.leds ?? Array.from({ length: 25 }, () => 0);
  const dpad = toRemote(frame).dpad;
  const [rx, ry] = dpad ? TILT[dpad] ?? [0, 0] : [0, 0];
  const s = frame?.sensors;
  const values: [string, number | undefined][] = [["light", s?.light], ["temp", s?.temp], ["sound", s?.sound], ["heading", s?.heading]];
  const a = !!frame?.a;
  const b = !!frame?.b;
  const logo = !!frame?.logo;
  return (
    <div className="pf-mb__mirror" data-testid="fs-mb-mirror">
      <p className="pf-mb__mirror-title">🔌 {t("student.microbit.mirror.title")}</p>
      <div className="pf-mb__mirror-stage">
        <svg viewBox={`0 0 ${W} ${H}`} className="pf-mb__mirror-svg" data-shake={!!frame?.shake}
             style={{ transform: `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg)` }}
             role="img" aria-label={t("student.microbit.mirror.leds")} data-testid="fs-mb-mirror-board">
          <defs>
            <filter id="pf-mb-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="4" result="b" />
              <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
            <clipPath id="pf-mb-board-shape"><rect x="6" y="6" width={W - 12} height={H - 12} rx="26" /></clipPath>
          </defs>
          <g clipPath="url(#pf-mb-board-shape)">
          {/* 판 */}
          <rect x="6" y="6" width={W - 12} height={H - 12} rx="26" fill="#111" />
          {/* 위 모서리 장식(시뮬레이터처럼 A · B 쪽 색 띠) */}
          <path d="M6 32 Q6 6 32 6 L118 6 L6 118 Z" fill="#1c7ed6" opacity="0.85" />
          <path d={`M${W - 6} 32 Q${W - 6} 6 ${W - 32} 6 L${W - 118} 6 L${W - 6} 118 Z`} fill="#e03131" opacity="0.85" />
          {/* 터치 로고 */}
          <g data-testid="fs-mb-mirror-logo" data-on={logo}>
            <rect x={W / 2 - 42} y="24" width="84" height="40" rx="20" fill="none" stroke={logo ? "#ffd43b" : "#fab005"} strokeWidth={logo ? 10 : 7} />
            <circle cx={W / 2 - 16} cy="44" r="6" fill={logo ? "#ffd43b" : "#fab005"} />
            <circle cx={W / 2 + 16} cy="44" r="6" fill={logo ? "#ffd43b" : "#fab005"} />
          </g>
          {/* 마이크 구멍 */}
          <circle cx={W / 2 + 66} cy="44" r="3" fill="none" stroke="#fab005" strokeWidth="1.5" />
          {/* LED 5×5 — 밝기 0~9 */}
          <g data-testid="fs-mb-mirror-leds">
            {leds.map((v, i) => {
              const x = LED0X + (i % 5) * LED_STEP;
              const y = LED0Y + Math.floor(i / 5) * LED_STEP;
              const on = v > 0;
              return (
                <g key={i} data-on={on}>
                  <rect x={x - 4} y={y - 9} width="8" height="18" rx="2" fill="#2b2b2b" />
                  {on ? <rect x={x - 5} y={y - 10} width="10" height="20" rx="3" fill="#ff2a2a" opacity={0.3 + (v / 9) * 0.7} filter="url(#pf-mb-glow)" /> : null}
                </g>
              );
            })}
          </g>
          {/* A · B 단추 */}
          {([["A", 70, a], ["B", W - 70, b]] as const).map(([label, cx, on]) => (
            <g key={label} data-testid={`fs-mb-mirror-${label.toLowerCase()}`} data-on={on}>
              <rect x={cx - 30} y="146" width="60" height="60" rx="8" fill="#9aa0a6" />
              {[[-22, -22], [22, -22], [-22, 22], [22, 22]].map(([dx, dy], k) => <circle key={k} cx={cx + dx!} cy={176 + dy!} r="4" fill="#5f6368" />)}
              <circle cx={cx} cy="176" r="19" fill={on ? "#ffd43b" : "#202124"} stroke={on ? "#f59f00" : "#000"} strokeWidth="3" />
              <text x={cx} y="236" textAnchor="middle" fill="#fff" fontSize="22" fontWeight="700">{label}</text>
            </g>
          ))}
          {/* 아래 금색 가장자리 핀 */}
          <rect x="6" y={H - 92} width={W - 12} height="86" fill="#d4a017" />
          {Array.from({ length: 40 }, (_, k) => 14 + k * 12).map((x) => <rect key={x} x={x} y={H - 34} width="6" height="28" fill="#b8860b" />)}
          {PINS.map(([label, cx]) => (
            <g key={label}>
              <circle cx={cx} cy={H - 60} r="26" fill="#d4a017" stroke="#b8860b" strokeWidth="3" />
              <circle cx={cx} cy={H - 60} r="17" fill="#111" />
              <text x={cx} y={H - 16} textAnchor="middle" fill="#111" fontSize="20" fontWeight="800">{label}</text>
            </g>
          ))}
          </g>
        </svg>
      </div>
      <p className="pf-mb__mirror-state">
        {frame?.shake ? <b>{t("student.microbit.shake")}</b> : null}{" "}
        {dpad ? t("student.microbit.mirror.tilt", { dir: ARROW[dpad] ?? dpad }) : null}
      </p>
      {values.some(([, v]) => v !== undefined) ? (
        <p className="pf-mb__mirror-state" data-testid="fs-mb-mirror-sensors">
          {values.filter(([, v]) => v !== undefined).map(([k, v]) => t(`student.microbit.sensor.value.${k}`, { v: v! })).join(" · ")}
        </p>
      ) : null}
      <p className="pf-mb__hint">{frame ? t("student.microbit.mirror.hint") : t("student.microbit.board4.waiting")}</p>
    </div>
  );
}

/**
 * 라디오 광장 게이트웨이(§6.7) — 꽂은 보드가 게이트웨이면 거울 대신. 교실 공중 라디오 ↔ 광장을 나른 수와 광장 연결을 보인다.
 */
export function GatewayMirror({ group, up, down, plaza }: { group: number; up: number; down: number; plaza: RadioStatus }) {
  const { t } = useI18n();
  return (
    <div className="pf-mb__mirror" data-testid="fs-mb-gateway">
      <p className="pf-mb__mirror-title">📡 {t("student.microbit.gateway.title")}</p>
      <p className="pf-mb__gateway-group" data-testid="fs-mb-gateway-group">{t("student.microbit.gateway.group", { g: group })}</p>
      <ul className="pf-mb__gateway-flow">
        <li data-testid="fs-mb-gateway-up">🛰️ {t("student.microbit.gateway.up", { n: up })}</li>
        <li data-testid="fs-mb-gateway-down">📻 {t("student.microbit.gateway.down", { n: down })}</li>
      </ul>
      <p className="pf-mb__mirror-state" data-testid="fs-mb-gateway-plaza">{t(`student.microbit.gateway.plaza.${plaza}`)}</p>
      <p className="pf-mb__hint">{t("student.microbit.gateway.hint")}</p>
    </div>
  );
}
